/**
 * Crypto Price Client
 *
 * Reads prices exclusively from the GraveForge server's /graveforge/crypto-prices
 * endpoint, which is backed by a server-side warm cache (see
 * server/services/priceService.js). No client-side fallback chain and no
 * hardcoded numeric fallbacks — when the server is unreachable or the cache
 * hasn't warmed yet, `error` is set and consumers show a "prices unavailable"
 * state rather than transacting at wrong amounts.
 */

async function fetchServerPrices() {
  const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3002';
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(`${serverUrl}/graveforge/crypto-prices`, {
      signal: controller.signal
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.success) {
      const err = new Error(result?.error || `Server error: ${response.status}`);
      err.warming = response.status === 503;
      throw err;
    }
    return result;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchCryptoPrices() {
  try {
    const result = await fetchServerPrices();
    return {
      SOL: result.prices?.solana?.usd ?? null,
      ETH: result.prices?.ethereum?.usd ?? null,
      CUSTOM: result.prices?.deads?.usd ?? null,
      fetchedAt: result.fetchedAt ?? null,
      sources: result.sources ?? null,
      error: null
    };
  } catch (error) {
    if (error.name !== 'AbortError' && error.message !== 'Failed to fetch') {
      console.warn('Live prices unavailable:', error.message);
    }
    return {
      SOL: null,
      ETH: null,
      CUSTOM: null,
      fetchedAt: null,
      sources: null,
      error: error.warming ? 'warming' : 'unavailable'
    };
  }
}

export function startPriceRefresh(callback, interval = 60000) {
  let intervalId;
  const refresh = async () => {
    const prices = await fetchCryptoPrices();
    callback(prices);
  };
  refresh();
  intervalId = setInterval(refresh, interval);
  return () => {
    if (intervalId) clearInterval(intervalId);
  };
}

export default {
  fetchCryptoPrices,
  startPriceRefresh
};
